import type { Point, NumericPoint } from "@incytestudios/ddv";
import { isFinitePoint, isNaturalPoint } from "../_common";
import type { DataSetWithOriginalPoints, SinglePumpChartContext } from "..";
import { convert } from "@/libs/units";
import { PercentageUnits } from "@/libs/units/percentage";
import { getPumpEfficiencyRatio } from "@/libs/calculations/pumps";

const getEfficiencyPoints = (
  context: SinglePumpChartContext,
  bhpDataSet: Point[],
  tdhDataSet: Point[]
) => {
  const { fluidDensitySpGr } = context;

  // Make sure we only calculate efficiency for flowrates that exist in both datasets
  const bhpFlowRates = new Set(bhpDataSet.map(pt => pt.x as number));
  const tdhFlowRates = new Set(tdhDataSet.map(pt => pt.x as number));
  const commonFlowrates = Array.from(bhpFlowRates).filter(x =>
    tdhFlowRates.has(x)
  );
  const efficiencyPoints = commonFlowrates.map((flowRate: number) => {
    const bhpPoint = bhpDataSet.find(pt => pt.x === flowRate);
    const tdhPoint = tdhDataSet.find(pt => pt.x === flowRate);
    const bhp = bhpPoint ? (bhpPoint.y as number) : 0;
    const tdh = tdhPoint ? (tdhPoint.y as number) : 0;
    return {
      x: flowRate,
      y: getPumpEfficiencyRatio(flowRate, tdh, fluidDensitySpGr, bhp)
    };
  });
  return efficiencyPoints;
};

export const getDataSetEfficiency = (
  context: SinglePumpChartContext,
  bhpDataSet: Point[],
  tdhDataSet: Point[]
): DataSetWithOriginalPoints => {
  const { baseXUnit, targetXUnit, targetXUnitLabel } = context;
  const data = getEfficiencyPoints(context, bhpDataSet, tdhDataSet);
  const convertedEfficiencyPoints = data
    .map<NumericPoint>(pt => ({
      x: convert(pt.x, baseXUnit, targetXUnit),
      y: convert(pt.y, PercentageUnits.Ratio, PercentageUnits.Percentage)
    }))
    .filter(isFinitePoint)
    .filter(isNaturalPoint);
  return {
    title: "Efficiency",
    xAxisLabel: `In Situ Flow Rate (${targetXUnitLabel})`,
    yAxisLabel: "Efficiency (%)",
    points: convertedEfficiencyPoints,
    originalPoints: data
  };
};
